import {
    useState,
    useRef,
    createRef,
    useCallback,
} from 'react'
import {
    useQuery,
    useMutation,
} from '@tanstack/react-query'
import {
    useLocation,
} from 'react-router-dom'

import backgroundCheckFormQuery from './services/background-check-form'
import submitBackgroundCheckFormMutation from './services/submit-background-check-form'
import saveApplicantProgressMutation from './services/save-applicant-progress'
import consentUploadMutation from './services/consent-upload'

import ApplicantDataForm from './Components/ApplicantDataForm'
import Welcome from './Components/Welcome'
import Summary from './Components/Summary'
import PublicHeader from '../../publicHeader'

import FullScreenDialog from "../../Components/FullScreenDialog"
import LocationOfResidence from './Components/LocationOfResidence'

import ApplicantDataFormContext from '../../Contexts/ApplicantDataFormContext'
import BackgroundCheckStepper from './Components/BackgroundCheckStepper'

import euCountries from './euCountries'
import DisclosureForElectronicConsent from './Components/DisclosureForElectronicConsent'
import DisclosureForInvestigateConsumerReport from './Components/DisclosureForInvestigateConsumerReport'
import DisclosureRegardingBackgroundInvestigation from './Components/DisclosureRegardingBackgroundInvestigation'
import AcknowledgmentAndAuthorization from './Components/AcknowledgmentAndAuthorization'
import NoticeStateCalifornia from './Components/StateNotices/NoticeStateCalifornia'
import NoticeStateMassachusetts from './Components/StateNotices/NoticeStateMassachusetts'
import NoticeStateMinnesota from './Components/StateNotices/NoticeStateMinnesota'
import NoticeStateNewJersey from './Components/StateNotices/NoticeStateNewJersey'
import NoticeStateOklahoma from './Components/StateNotices/NoticeStateOklahoma'
import NoticeStateWashington from './Components/StateNotices/NoticeStateWashington'
import NoticeCityNewYorkCity from './Components/CityNotices/NoticeCityNewYorkCity'
import NoticeStateNewYork2 from './Components/StateNotices/NoticeStateNewYork2'
import Overlay from '../../Components/Overlay'

import { getElementWithInlineStyles } from '../../helpers/component-helper'
import PdfViewer from './Components/PdfViewer'
import ExpiredPage from './Components/ExpiredPage'
import IndiaConsentForm from './Components/IndiaConsentForm'

const BackgroundCheckForm = () => {

    const location = useLocation()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')
    const referenceNumber = queryParams.get('referenceNumber')

    const refToPrint = useRef()

    const {
        data,
        error,
        isFetching,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['background-check-form', key || referenceNumber],
        queryFn: () => backgroundCheckFormQuery(key, referenceNumber),
        refetchOnWindowFocus: false,
    })

    const {
        error: mutationSubmitError,
        isPending: mutationSubmitIsPending,
        mutate: submitBackgroundCheckForm,
        data: mutationSubmitData,
    } = useMutation({
        mutationFn: submitBackgroundCheckFormMutation,
    })

    const {
        error: mutationConsentUploadError,
        isPending: mutationConsentUploadIsPending,
        mutate: consentUpload,
        data: mutationConsentUploadData,
    } = useMutation({
        mutationFn: consentUploadMutation,
        onSuccess: async (d) => {
            const payload = d.payloadToSaveApplicantProgress
            const pageType = d.pageType
            const url = d.url

            const page = payload?.data?.find((d => d.type === pageType))

            if (page?.data) {
                page.data.htmlUrl = url
            }

            saveApplicantProgress(payload)
        },
    })

    let applicantDataPageIndex
    let applicantDataItemCount = 0
    let applicantDataCurrentIndex = 0

    let acknowledgmentAndAuthorizationPageIndex
    let locationOfResidencePageIndex

    const avoidSaveApplicantProgressOnsuccessRef = useRef(false)

    const {
        error: mutationSaveError,
        isPending: mutationSaveIsPending,
        mutate: saveApplicantProgress,
        mutateAsync: saveApplicantProgressAsync,
    } = useMutation({
        mutationFn: saveApplicantProgressMutation,
        onSuccess: async (d) => {

            if (avoidSaveApplicantProgressOnsuccessRef.current) {
                avoidSaveApplicantProgressOnsuccessRef.current = false
                return
            }

            if (currentPageIndex === locationOfResidencePageIndex) {
                await refetch()
            }

            const nextPageIndex = currentPageIndex + 1

            // continue next page
            setCurrentPageIndex(nextPageIndex)

            if (nextPageIndex < applicantDataPageIndex) {
                setCurrentStep(0)
            }
            else {
                if (nextPageIndex === data.Pages.length - 1) {
                    setCurrentStep(1 + applicantDataItemCount)
                }
                else {
                    await refetch()
                    setCurrentStep(1 + applicantDataCurrentIndex)
                }
            }
        }
    })

    const [currentPageIndex, setCurrentPageIndex] = useState(0)

    const pageRefs = useRef([])

    const [currentStep, setCurrentStep] = useState(0)

    const [isEuResidence, setIsEuResidence] = useState(false)
    const [isUsResidence, setIsUsResidence] = useState(false)

    let currentRef = pageRefs.current[currentPageIndex]

    if (!currentRef) {
        currentRef = createRef()
        pageRefs.current[currentPageIndex] = currentRef
    }


    const saveProgressRef = useRef()

    saveProgressRef.current = async () => {

        const currentPageData = currentRef.current.validateAndGetData();
        currentPage.data = currentPageData.value;

        const dataToSubmit = data.Pages.map(page => ({
            type: page.type,
            data: page.data,
        }))

        const payload = {
            key,
            referenceNumber,
            data: dataToSubmit,
        }

        avoidSaveApplicantProgressOnsuccessRef.current = true;

        return saveApplicantProgressAsync(payload).then(() => refetch());
    }

    const saveProgress = async () => {
        await saveProgressRef.current?.()
    }

    const contextRef = useRef({
        requestKey: key,
        referenceNumber,
        saveProgress,
    })

    if (isLoading) return <div className="flex justify-center items-center min-h-screen my-4">
        <span className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
    </div>
    if (error) return <div>Error: {error.message}</div>

    if (data?.noFormMessage) {

        if (data?.status === "expired") {
            return (
                <div className="h-screen flex flex-col bg-gray-50">

                    <PublicHeader />

                    <div className="flex-grow flex items-center justify-center">
                        <ExpiredPage company={data?.company} />
                    </div>

                </div>

            )
        }

        return (
            <div className="h-screen flex flex-col bg-gray-50">

                <PublicHeader />

                <p className="mt-1 text-base py-4 mx-auto">
                    {data.noFormMessage}
                </p>

            </div>
        )
    }

    const currentPage = data.Pages[currentPageIndex]

    const locationOfResidenceHandler = (locationOfResidence) => {
        let isLocationEuResidence = false
        let isLocationUsResidence = false

        if (locationOfResidence.country === "United States") {
            isLocationUsResidence = true
        }
        else {
            isLocationEuResidence = euCountries.includes(locationOfResidence.country)
        }
        setIsEuResidence(isLocationEuResidence)
        setIsUsResidence(isLocationUsResidence)
    }

    const steps = [
        "Consent",
    ]

    for (const [index, page] of data.Pages.entries()) {
        if (page.type === "applicant_data") {
            applicantDataPageIndex = index
            for (const field of page.fields) {
                applicantDataItemCount += 1
                steps.push(field.title)
            }
        }
        else {

            if (page.type === "acknowledgment_and_authorization") {
                acknowledgmentAndAuthorizationPageIndex = index
            }
            else if (page.type === "location_of_residence") {
                locationOfResidencePageIndex = index
            }
        }
    }

    if (currentPage.type === "summary") {
        currentPage.data = data.Pages[applicantDataPageIndex]
    }

    steps.push("Summary")

    const handleNext = () => {

        if (currentRef.current?.onNext) {

            const nextHandled = currentRef.current.onNext()

            if (nextHandled) {
                return
            }
        }

        const nextPageIndex = currentPageIndex + 1
        const currentPageData = currentRef.current.validateAndGetData()
        if (!currentPageData.isValid) {
            return
        }
        currentPage.data = currentPageData.value

        const dataToSubmit = data.Pages.map(page => {
            return {
                type: page.type,
                data: page.data,
            }
        })

        const payload = {
            key,
            referenceNumber,
            data: dataToSubmit,
        }

        // submit form
        if (nextPageIndex >= data.Pages.length) {

            submitBackgroundCheckForm(payload)

            return
        }

        if (refToPrint.current) {

            syncCheckedAttributes(refToPrint.current)

            consentUpload({
                dataForUploadUrl: {
                    key,
                    referenceNumber,
                    consentType: currentPage.type,
                },
                file: new File([getElementWithInlineStyles(refToPrint.current),], `${currentPage.type}.html`, { type: "text/html" }),
                payloadToSaveApplicantProgress: payload,
                pageType: currentPage.type,
            })

            return
        }

        saveApplicantProgress(payload)

    }

    const refetchFormHandler = () => {
        refetch()
    }

    const handlePrev = () => {
        const prevPageIndex = currentPageIndex - 1
        if (prevPageIndex < 0) {
            return
        }
        const currentPageData = currentRef.current.validateAndGetData()
        currentPage.data = currentPageData.value
        setCurrentPageIndex(prevPageIndex)

        if (prevPageIndex < applicantDataPageIndex) {
            setCurrentStep(0)
        }
        else {
            setCurrentStep(1 + applicantDataCurrentIndex)
        }

    }

    const applicantDataCurrentIndexHandler = async (currentIndex) => {
        applicantDataCurrentIndex = currentIndex
        setCurrentStep(1 + applicantDataCurrentIndex)

        const currentPageData = currentRef.current.validateAndGetData()
        currentPage.data = currentPageData.value

        const dataToSubmit = data.Pages.map(page => {
            return {
                type: page.type,
                data: page.data,
            }
        })

        const payload = {
            key,
            referenceNumber,
            data: dataToSubmit,
        }

        avoidSaveApplicantProgressOnsuccessRef.current = true

        await saveApplicantProgressAsync(payload)
        await refetch()

    }

    const handleComplete = () => {
        window.location.href = "https://verifiedid.co"
    }

    let nextButtonText = currentPageIndex === data.Pages.length - 1 ? "Submit" :
        currentPageIndex === acknowledgmentAndAuthorizationPageIndex ? "Sign Acknowledgment" :
            "Next"

    return (
        <div className="h-screen flex flex-col bg-gray-50">

            <PublicHeader referenceNumber={data.referenceNumber} />

            {
                currentPage.type !== "welcome" && (
                    <BackgroundCheckStepper steps={steps} currentStep={currentStep} />
                )
            }

            <ApplicantDataFormContext.Provider value={contextRef}>

                <div className="flex flex-col flex-grow items-center justify-center">
                    {
                        currentPage.type === "welcome" &&
                        <Welcome
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            handleNext={handleNext}
                        />
                    }
                    {
                        currentPage.type === "location_of_residence" &&
                        <LocationOfResidence
                            ref={currentRef}
                            data={currentPage.data}
                            locationOfResidenceHandler={locationOfResidenceHandler}
                        />
                    }
                    {
                        currentPage.type === "india_consent" &&
                        <IndiaConsentForm
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "disclosure_for_electronic_consent" &&
                        <DisclosureForElectronicConsent
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            employmentLocation={currentPage.employmentLocation}
                            refetchFormHandler={refetchFormHandler}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "disclosure_for_investigate_consumer_report" &&
                        <DisclosureForInvestigateConsumerReport
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "disclosure_regarding_background_investigation" &&
                        <DisclosureRegardingBackgroundInvestigation
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "summary_of_rights_under_fcra" &&
                        <PdfViewer
                            title={"Summary of Your Rights Under the Fair Credit Reporting Act"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "acknowledgment_and_authorization" &&
                        <AcknowledgmentAndAuthorization
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_california" &&
                        <NoticeStateCalifornia
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_massachusetts" &&
                        <NoticeStateMassachusetts
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_minnesota" &&
                        <NoticeStateMinnesota
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_new_jersey" &&
                        <NoticeStateNewJersey
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_oklahoma" &&
                        <NoticeStateOklahoma
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_washington" &&
                        <NoticeStateWashington
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_state_new_york" &&
                        <PdfViewer
                            title={"New York Correction Law Article 23-A"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_state_new_york_2" &&
                        <NoticeStateNewYork2
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_city_san_francisco" &&
                        <PdfViewer
                            title={"San Francisco Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_los_angeles" &&
                        <PdfViewer
                            title={"Los Angeles Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_new_york_city" &&
                        <NoticeCityNewYorkCity
                            ref={currentRef}
                            data={currentPage.data}
                            company={currentPage.company}
                            willVerificationBeApplied={currentPage.willVerificationBeApplied}
                            refToPrint={refToPrint}
                        />
                    }
                    {
                        currentPage.type === "notice_city_new_york_city_rights" &&
                        <PdfViewer
                            title={"New York City Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_new_york_city_rights_es" &&
                        <PdfViewer
                            title={"New York City Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_new_york_city_rights_man" &&
                        <PdfViewer
                            title={"New York City Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_philadelphia" &&
                        <PdfViewer
                            title={"Philadelphia Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_seattle" &&
                        <PdfViewer
                            title={"Seattle Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "notice_city_washington_dc" &&
                        <PdfViewer
                            title={"Washington, DC Notice"}
                            ref={currentRef}
                            pdfUrl={currentPage.url}
                        />
                    }
                    {
                        currentPage.type === "applicant_data" &&
                        <ApplicantDataForm
                            formFields={currentPage.fields}
                            ref={currentRef} data={currentPage.data}
                            currentIndexHandler={applicantDataCurrentIndexHandler}
                        />
                    }
                    {
                        currentPage.type === "summary" &&
                        <div className='w-full max-w-4xl'>
                            <Summary ref={currentRef} data={currentPage.data} />
                        </div>
                    }
                </div>

            </ApplicantDataFormContext.Provider>



            {(mutationSubmitError || mutationSaveError) && (
                <p className="mt-1 text-base text-red-600 py-4 mx-auto">
                    Error
                </p>
            )}

            {
                currentPage.type !== "welcome" && (
                    <div className="flex justify-end gap-6 p-4 border-t">
                        <button
                            onClick={handlePrev}
                            disabled={currentPageIndex === 0}
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            Prev
                        </button>
                        <button
                            onClick={handleNext}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            {nextButtonText}
                        </button>
                    </div>
                )
            }

            {/* Overlay */}
            {
                (mutationSubmitIsPending || mutationSaveIsPending || mutationConsentUploadIsPending || isFetching) &&
                <Overlay />
            }

            {
                mutationSubmitData &&
                <FullScreenDialog
                    text={
                        <>
                            <div>
                                <div className="mb-4 font-semibold text-center text-xlg">
                                    Thank you for completing your Background Check application!
                                </div>
                                <div className="mb-2 text-sm">
                                    Your information has been successfully submitted to VerifiedID for processing.
                                </div>
                                <div className="mb-4 text-sm">
                                    We will notify you once the background check is complete, or if any additional information is required.
                                </div>
                                <div className='mb-4 text-sm'>
                                    You may now close this window.
                                </div>
                            </div>
                        </>
                    }
                    onSubmit={handleComplete}
                    submitText="Close Window"
                />
            }

        </div>

    )
}

export default BackgroundCheckForm

function syncCheckedAttributes(container) {
    const inputs = container.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((input) => {
        if (input.checked) {
            input.setAttribute("checked", "checked");
        } else {
            input.removeAttribute("checked");
        }
    });
}
